import { useInfiniteQuery } from 'react-query';
import HttpService from 'services/httpService';
import { ENDPOINT_BASE_URL } from './constants';
import { useAuth } from './useAuth';
import { PaginatedAppointmentsResponse, AppointmentListParams } from 'models/AppointmentModels';
import { useMemo } from 'react';

export const paginatedAppointmentQueryKeys = {
    paginatedAppointmentsByOrgKey: (orgId: string) => ['paginatedAppointments', { orgId }],
};

export type PaginatedAppointmentsQueryCache = {
    data: PaginatedAppointmentsResponse;
    ts: number;
} | undefined;

interface UsePaginatedAppointmentDataOptions {
    preventRefetch?: boolean;
    limit?: number;
    includeTotalCount?: boolean;
    enabled?: boolean;
    polling?: boolean; // Note: Will refetch ALL loaded pages every 10 seconds
    includeExams?: boolean;
    searchQuery?: string; // Add search support
    startDate?: Date | null; // Add date range support
    endDate?: Date | null;
}

export const usePaginatedAppointmentData = (options?: UsePaginatedAppointmentDataOptions) => {
    const { accessToken, user } = useAuth();

    const getPaginatedAppointmentsByOrg = async ({ pageParam }: { pageParam?: string }) => {
        if (!user || !accessToken) return;

        const params: AppointmentListParams = {
            org_id: user.orgs[0].org_id,
            include_exams: options?.includeExams || false,
            limit: options?.limit || 15,
            include_total_count: options?.includeTotalCount || false,
        };

        if (pageParam) {
            params.next_token = pageParam;
        }

        const queryParams = new URLSearchParams();

        if (params.include_exams) {
            queryParams.append('include_exams', params.include_exams.toString());
        }
        if (params.org_id) {
            queryParams.append('org_id', params.org_id);
        }
        if (params.limit) {
            queryParams.append('limit', params.limit.toString());
        }
        if (params.next_token) {
            queryParams.append('next_token', params.next_token);
        }
        if (params.include_total_count) {
            queryParams.append('include_total_count', params.include_total_count.toString());
        }
        if (options?.searchQuery) {
            queryParams.append('q', options.searchQuery);
        }
        if (options?.startDate) {
            queryParams.append('appointment_date_from', options.startDate.toISOString().split('T')[0]);
        }
        if (options?.endDate) {
            queryParams.append('appointment_date_to', options.endDate.toISOString().split('T')[0]);
        }

        const data = await HttpService.get<PaginatedAppointmentsResponse>({
            url: `${ENDPOINT_BASE_URL}/appointments/?${queryParams.toString()}`,
            token: accessToken,
        });
        return data;
    };

    // Paginated appointments by organization
    const {
        data: paginatedAppointmentsByOrgData,
        error: appointmentsError,
        isFetching: appointmentsLoading,
        isLoading: appointmentsInitialLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch: refetchAppointments,
    } = useInfiniteQuery(
        [
            ...paginatedAppointmentQueryKeys.paginatedAppointmentsByOrgKey(user?.orgs[0]?.org_id || ''),
            options?.searchQuery || 'no-search',
            options?.startDate?.toISOString() || 'no-start-date',
            options?.endDate?.toISOString() || 'no-end-date'
        ],
        getPaginatedAppointmentsByOrg,
        {
            refetchOnMount: !options?.preventRefetch,
            enabled: options?.enabled !== false && !!user && !!accessToken,
            getNextPageParam: (lastPage) => lastPage?.data?.next_token || undefined,
            refetchInterval: options?.polling ? 10000 : undefined, // Poll every 10 seconds if polling is true
            keepPreviousData: true, // Keep prior pages visible while new search/date params load
        },
    );

    // Flatten all pages into a single array of appointments
    const allAppointments = useMemo(() => {
        if (!paginatedAppointmentsByOrgData?.pages) return [];
        const flattened = paginatedAppointmentsByOrgData.pages.flatMap(page => page?.data?.appointments || []);
        return flattened;
    }, [paginatedAppointmentsByOrgData]);

    // Calculate total count from all pages if includeTotalCount is enabled
    const totalCount = useMemo(() => {
        if (!options?.includeTotalCount || !paginatedAppointmentsByOrgData?.pages) return undefined;
        // Return the total_count from the first page (it should be the same across all pages)
        const count = paginatedAppointmentsByOrgData.pages[0]?.data?.total_count;
        return count;
    }, [paginatedAppointmentsByOrgData, options?.includeTotalCount]);

    return {
        allAppointments,
        appointmentsError,
        appointmentsLoading,
        appointmentsInitialLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetchAppointments,
        totalCount,
    };
}; 